import React, { useState, useEffect } from 'react';
import Breadcrumbs from '@grnhse/seedling/lib/birch/components/breadcrumbs';
import { SectionTitle } from '@grnhse/seedling/lib/birch/typography';
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@grnhse/seedling/lib/birch/components/tabs';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import { GlobalWhiteBackgroundImportant } from 'redesign_2022/global_white_background';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  type UserContextValue,
  type TabKey,
  TABS,
  UserContextProvider,
} from 'talent_matching/contexts/user_context';
import { type TalentMatchingTabContextValue } from 'talent_matching/contexts/talent_matching_tab_context';
import { useDocumentTitle } from 'shared/utils/react_router_helper';
import {
  TalentMatchingFilters,
  RediscoveryProps,
  MyGreenhouseProps,
} from 'talent_matching/types';
import MyGreenhouse from 'talent_matching/pages/my_greenhouse';
import TalentMatching, {
  type InitialSavedFilterSet,
} from 'talent_matching/pages/talent_matching';
import TalentRediscovery from 'talent_matching/pages/talent_rediscovery';
import TalentMatchingQueryClientProvider from 'talent_matching/talent_matching_query_client';
import styled from 'styled-components';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import {
  RankingInProgressProvider,
  type RankingTrigger,
} from 'talent_matching/contexts/ranking_in_progress_context';
import {
  CalibrationModalProvider,
  type NudgeCalloutState,
} from 'talent_matching/contexts/calibration_modal_context';
import CalibrationButton from 'talent_matching/components/calibration/calibration_button';
import CalibrationModal from 'talent_matching/components/calibration/calibration_modal';
import CalibrationNudgeModal from 'talent_matching/components/modals/calibration_nudge_modal';
import { CalibrationSuggestion } from 'talent_matching/types';
import { AdvancedFiltersProvider } from 'talent_matching/contexts/advanced_filters_context';

const t = withLocalePrefix('talent_matching');

const StyledTabs = styled(Tabs)`
  .birch-tab-list-animated-wrapper > div[role='tablist'] {
    column-gap: ${Tokens.spacing['3']};
  }
`;

const SectionTitleWrapper = styled.span`
  h3 {
    font-weight: 400;
  }
`;

const ALL_TABS = [
  { key: TABS.APP_REVIEW, segment: '', label: 'tabs.app_review' },
  { key: TABS.REDISCOVERY, segment: '/rediscovery', label: 'tabs.rediscovery' },
  {
    key: TABS.MY_GREENHOUSE,
    segment: '/mygreenhouse',
    label: 'tabs.my_greenhouse',
  },
] as const;

const getBasePath = () => {
  return window.location.pathname
    .replace('/rediscovery', '')
    .replace('/mygreenhouse', '');
};

const getTabIndexFromUrl = (
  visibleTabs: ReadonlyArray<typeof ALL_TABS[number]>
) => {
  const { pathname } = window.location;
  const matchedSegment =
    ALL_TABS.slice(1).find((tab) => pathname.endsWith(tab.segment))?.segment ??
    '';

  const idx = visibleTabs.findIndex((tab) => tab.segment === matchedSegment);
  if (idx === -1) {
    window.history.replaceState(null, '', getBasePath());
    return 0;
  }
  return idx;
};

type Props = {
  userContextProps: Omit<UserContextValue, 'activeTab'>;
  talentMatchingProps: Omit<
    TalentMatchingTabContextValue,
    'activeSidePanelTab' | 'setActiveSidePanelTab' | 'setSavedSearchOptions'
  > & {
    initialFilters: Partial<TalentMatchingFilters>;
    initialSavedFilterSet: InitialSavedFilterSet | null;
    previouslyCalibrated: boolean;
    rankingTrigger: RankingTrigger | null;
    pollIndexingOnLoad: boolean;
  };
  rediscoveryProps?: RediscoveryProps;
  showMyGreenhouseTab: boolean;
  myGreenhouseProps?: MyGreenhouseProps;
};

const TalentMatchingPageContent = ({
  userContextProps,
  talentMatchingProps,
  rediscoveryProps,
  showMyGreenhouseTab,
  myGreenhouseProps,
}: Props) => {
  const visibleTabs = ALL_TABS.filter(
    (tab) => showMyGreenhouseTab || tab.key !== TABS.MY_GREENHOUSE
  );

  const [activeTabIndex, setActiveTabIndex] = useState(() =>
    getTabIndexFromUrl(visibleTabs)
  );

  const { jobTitle } = talentMatchingProps;

  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
    const segment = visibleTabs[index].segment;
    window.history.pushState(null, '', `${getBasePath()}${segment}`);
  };

  useEffect(() => {
    const onPopState = () => {
      setActiveTabIndex(getTabIndexFromUrl(visibleTabs));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMyGreenhouseTab]);

  const activeTab = visibleTabs[activeTabIndex].key as TabKey;

  const { previouslyCalibrated, rankingTrigger, ...talentMatchingTabProps } =
    talentMatchingProps;

  const [showCalibrationModal, openCalibrationModal, closeCalibrationModal] =
    useModal();
  const [nudgeCalloutState, setNudgeCalloutState] =
    useState<NudgeCalloutState>(null);
  const [showNudgeReviewModal, openNudgeReviewModal, closeNudgeReviewModal] =
    useModal();
  const [pendingNudgeSuggestions, setPendingNudgeSuggestions] = useState<
    CalibrationSuggestion[] | null
  >(null);

  const onReviewModalApply = (selected: CalibrationSuggestion[]) => {
    if (selected.length > 0) {
      setPendingNudgeSuggestions(selected);
    }
    setNudgeCalloutState('applied');
  };

  const renderTabPanelContent = (key: TabKey) => {
    switch (key) {
      case TABS.APP_REVIEW:
        return <TalentMatching {...talentMatchingTabProps} />;
      case TABS.REDISCOVERY:
        if (!rediscoveryProps) {
          return null;
        }
        return <TalentRediscovery {...rediscoveryProps} />;
      case TABS.MY_GREENHOUSE:
        if (!myGreenhouseProps) {
          return null;
        }
        return <MyGreenhouse {...myGreenhouseProps} />;
    }
  };

  return (
    <AdvancedFiltersProvider
      value={talentMatchingTabProps.advancedFilterConfig}
    >
      <UserContextProvider value={{ ...userContextProps, activeTab }}>
        <RankingInProgressProvider
          rankingTrigger={rankingTrigger}
          previouslyCalibrated={previouslyCalibrated}
          rankingInProgressPath={
            talentMatchingProps.paths.rankingInProgressPath
          }
        >
          <CalibrationModalProvider
            value={{
              openCalibrationModal,
              closeCalibrationModal,
              showCalibrationModal,
              paths: talentMatchingTabProps.paths,
              jobTitle: jobTitle ?? '',
              canCreateCalibrationSetting:
                talentMatchingTabProps.canCreateCalibrationSetting,
              canEditAndDeleteTalentMatchingCalibration:
                talentMatchingTabProps.canEditAndDeleteTalentMatchingCalibration,
              nudgeCalloutState,
              setNudgeCalloutState,
              openNudgeReviewModal: () => {
                closeCalibrationModal();
                openNudgeReviewModal();
              },
              pendingNudgeSuggestions,
              setPendingNudgeSuggestions,
            }}
          >
            <Stack spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ marginTop: 1 }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <SectionTitle large>{t('header')}</SectionTitle>
                  <SectionTitleWrapper>
                    <SectionTitle large>{jobTitle}</SectionTitle>
                  </SectionTitleWrapper>
                </Stack>
                <CalibrationButton
                  rankingDisabledReason={
                    talentMatchingTabProps.rankingDisabledReason
                  }
                />
              </Stack>
              {visibleTabs.length > 1 ? (
                <StyledTabs
                  index={activeTabIndex}
                  onChange={handleTabChange}
                  data-provides="talent-matching-tabs"
                >
                  <TabList>
                    {visibleTabs.map((tab) => (
                      <Tab
                        key={tab.key}
                        data-provides={`talent-matching-${tab.key}-tab`}
                      >
                        {t(tab.label)}
                      </Tab>
                    ))}
                  </TabList>
                  <Divider />
                  <TabPanels>
                    {visibleTabs.map((tab) => (
                      <TabPanel key={tab.key}>
                        {renderTabPanelContent(tab.key)}
                      </TabPanel>
                    ))}
                  </TabPanels>
                </StyledTabs>
              ) : (
                <TalentMatching {...talentMatchingTabProps} />
              )}
            </Stack>
            <CalibrationModal
              isOpen={showCalibrationModal}
              onClose={() => {
                closeCalibrationModal();
                setNudgeCalloutState(null);
              }}
            />
            <CalibrationNudgeModal
              isOpen={showNudgeReviewModal}
              onClose={closeNudgeReviewModal}
              canCreateCalibrationSetting={
                talentMatchingTabProps.canCreateCalibrationSetting
              }
              openCalibrationModal={openCalibrationModal}
              onApplySuggestions={onReviewModalApply}
              variant="review"
            />
          </CalibrationModalProvider>
        </RankingInProgressProvider>
      </UserContextProvider>
    </AdvancedFiltersProvider>
  );
};

const TalentMatchingPage = (props: Props) => {
  useDocumentTitle(t('document_title'));
  const { paths, jobTitle } = props.talentMatchingProps;

  return (
    <>
      <GlobalWhiteBackgroundImportant />
      <TalentMatchingQueryClientProvider>
        <Stack spacing={1}>
          <Breadcrumbs
            current={t('title')}
            crumbs={[
              {
                label: t('breadcrumbs.all_jobs'),
                href: paths.allJobsPath,
              },
              {
                label: jobTitle,
                href: paths.jobDashboardPath,
              },
            ]}
          />
          <TalentMatchingPageContent {...props} />
        </Stack>
      </TalentMatchingQueryClientProvider>
    </>
  );
};

export default TalentMatchingPage;
